type RuntimeEnv = Record<string, string | undefined>;

/** Keep in sync with normalizeWmcRootPath in config-overrides.js */
function normalizeWmcRootPath(raw: string | undefined): string {
    if (raw === undefined || raw === null) {
        return "";
    }
    const trimmed = String(raw).trim();
    if (trimmed === "") {
        return "";
    }
    let start = 0;
    let end = trimmed.length;
    while (start < end && trimmed[start] === "/") start++;
    while (end > start && trimmed[end - 1] === "/") end--;
    const core = trimmed.slice(start, end);
    if (core === "") {
        return "";
    }
    return `/${core}`;
}

declare global {
    interface Window {
        _env_?: RuntimeEnv;
    }
}

const getRuntimeEnv = (): RuntimeEnv => {
    // Runtime values are injected by /env.js (window._env_) when running in browser.
    if (globalThis.window === undefined) {
        return {};
    }

    return globalThis.window._env_ || {};
};

const getEnv = (key: string): string | undefined => {
    const runtimeValue = getRuntimeEnv()[key];

    // Priority: runtime env.js value first, then build-time process.env fallback.
    if (runtimeValue !== undefined && runtimeValue !== "") {
        return runtimeValue;
    }

    return process.env[key];
};

export const APP_CONFIG = {
    // Using getEnv allows local Docker deployments to override REACT_APP_* at container start.
    APP_AUTH_PROVIDER: getEnv("REACT_APP_AUTH_PROVIDER") || "cognito",
    APP_AWS_POOL_ID: getEnv("REACT_APP_AWS_POOL_ID") || "",
    APP_AWS_CLIENT_ID: getEnv("REACT_APP_AWS_CLIENT_ID") || "",
    APP_IS_ONPREM: getEnv("REACT_APP_IS_ONPREM") === "true" || false,
    APP_KEYCLOAK_BASE_URL: getEnv("REACT_APP_KEYCLOAK_BASE_URL") || "",
    APP_KEYCLOAK_REALM: getEnv("REACT_APP_KEYCLOAK_REALM") || "",
    APP_KEYCLOAK_CLIENT_ID: getEnv("REACT_APP_KEYCLOAK_CLIENT_ID") || "",
    APP_DASHBOARD_API: getEnv("REACT_APP_DASHBOARD_API") || "",
    APP_DISABLE_MISSING_DATA: (getEnv("REACT_APP_DISABLE_MISSING_DATA") || "false") === "true" || false,
    APP_SIGNUP_LOGIN_DELAY_SECONDS: Number(getEnv("REACT_APP_SIGNUP_LOGIN_DELAY_SECONDS") ?? 0) || 0,
    // asset URLs. It is NOT a runtime override , so no getEnv.
    APP_PUBLIC_URL: normalizeWmcRootPath(process.env.WMC_ROOT_PATH),
};

// Validate configuration at app startup
const validateConfig = () => {
    const isOnPrem = APP_CONFIG.APP_IS_ONPREM;
    const authProvider = APP_CONFIG.APP_AUTH_PROVIDER.toLowerCase();

    // If NOT on-prem, auth provider MUST be cognito
    if (!isOnPrem && authProvider !== "cognito") {
        throw new Error(
            `Invalid configuration: When REACT_APP_IS_ONPREM is false, ` +
            `REACT_APP_AUTH_PROVIDER must be "cognito". Current value: "${authProvider}".Please update environemnt.`
        );
    }

    // If on-prem, auth provider can be keycloak or local
    if (isOnPrem && !["local", "keycloak"].includes(authProvider)) {
        throw new Error(
            `Invalid configuration: REACT_APP_AUTH_PROVIDER must be "local" or "keycloak". ` +
            `Current value: "${authProvider}".Please update environemnt.`
        );
    }
};

validateConfig();

export { validateConfig };
